import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import AboutModal from '../components/AboutModal';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminPanel({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'users');
  
  // Data states
  const [users, setUsers] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [speakerFilter, setSpeakerFilter] = useState('');
  const [selectedSpeakerIds, setSelectedSpeakerIds] = useState(new Set());
  const [consolidateDialog, setConsolidateDialog] = useState({ open: false, speakers: [], masterId: null });
  const [clubs, setClubs] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedTopicIds, setSelectedTopicIds] = useState(new Set());
  const [topicConsolidateDialog, setTopicConsolidateDialog] = useState({ open: false, topics: [], masterId: null });
  const [talks, setTalks] = useState([]);
  const [talkSearch, setTalkSearch] = useState('');
  const [selectedTalkIds, setSelectedTalkIds] = useState(new Set());
  const [addTalkDialog, setAddTalkDialog] = useState({ open: false });
  const [addTalkForm, setAddTalkForm] = useState({
    speakerMode: 'existing', speakerId: '', speakerName: '', speakerEmail: '',
    topicMode: 'existing', topicId: '', topicName: '', topicDescription: '',
    clubName: '', eventDate: '',
  });
  const [addTalkError, setAddTalkError] = useState(null);
  const [speakerPopoverOpen, setSpeakerPopoverOpen] = useState(false);
  const [topicPopoverOpen, setTopicPopoverOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [dbStatus, setDbStatus] = useState({ status: 'checking', message: 'Checking...' });
  
  // Dialog states
  const [dialog, setDialog] = useState({ open: false, type: '', data: null });
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState(null);
  const [dialogMessage, setDialogMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [talksUploadFile, setTalksUploadFile] = useState(null);
  const [clubsUploadFile, setClubsUploadFile] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [topicSearch, setTopicSearch] = useState('');
  const [gmailStatus, setGmailStatus] = useState({ configured: false, authenticated: false, sender_email: '' });

  // Helper function to get valid token or trigger logout
  const getValidToken = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      showMessage('error', 'Session expired. Please log in again.');
      setTimeout(() => onLogout(), 1500);
      return null;
    }
    return token;
  }, [onLogout]);

  // Helper to handle auth errors and logout if needed
  const handleAuthError = useCallback((err) => {
    if (err.response?.status === 401) {
      showMessage('error', 'Session expired. Please log in again.');
      setTimeout(() => onLogout(), 1500);
      return true;
    }
    return false;
  }, [onLogout]);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    // Always fetch clubs on component mount for the user creation dropdown
    fetchClubs();
    // Fetch initial database status
    checkDatabaseStatus();
    // Fetch Gmail status
    fetchGmailStatus();
    
    // Check for Gmail OAuth callback results
    const gmailSuccess = searchParams.get('gmail_success');
    const gmailError = searchParams.get('gmail_error');
    
    if (gmailSuccess) {
      showMessage('success', 'Gmail connected successfully! Verification emails will now be sent.');
      setSearchParams({ tab: 'upload' });
      fetchGmailStatus();
    } else if (gmailError) {
      const errorMessages = {
        'invalid_state': 'OAuth session expired. Please try again.',
        'state_expired': 'OAuth session expired. Please try again.',
        'callback_failed': 'Failed to complete Gmail authorization. Please try again.',
        'missing_params': 'Invalid OAuth response. Please try again.',
      };
      showMessage('error', errorMessages[gmailError] || `Gmail connection failed: ${gmailError}`);
      setSearchParams({ tab: 'upload' });
    }
    
    // Poll database status every 10 seconds
    const statusInterval = setInterval(() => {
      checkDatabaseStatus();
    }, 10000);
    
    return () => clearInterval(statusInterval);
  }, []);

  const checkDatabaseStatus = async () => {
    try {
      const response = await axios.get(`${API}/database-status`);
      setDbStatus(response.data);
    } catch (err) {
      setDbStatus({
        status: 'disconnected',
        message: 'Unable to check database status',
        timestamp: new Date().toISOString()
      });
    }
  };

  const fetchGmailStatus = async () => {
    try {
      const headers = getHeaders();
      if (!headers.Authorization) return;
      const response = await axios.get(`${API}/oauth/gmail/status`, { headers });
      setGmailStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch Gmail status:', err);
    }
  };

  const handleGmailConnect = async () => {
    try {
      const headers = getHeaders();
      if (!headers.Authorization) {
        showMessage('error', 'You must be logged in to connect Gmail');
        return;
      }
      const response = await axios.get(`${API}/oauth/gmail/authorize`, { headers });
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (err) {
      console.error('Gmail connect error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
      showMessage('error', `Failed to start Gmail authorization: ${errorMsg}`);
    }
  };

  const handleGmailDisconnect = async () => {
    setConfirmDialog({
      open: true,
      title: 'Disconnect Gmail',
      message: 'Are you sure you want to disconnect Gmail? Verification emails will no longer be sent.',
      onConfirm: async () => {
        try {
          const headers = getHeaders();
          await axios.delete(`${API}/oauth/gmail/disconnect`, { headers });
          showMessage('success', 'Gmail disconnected');
          fetchGmailStatus();
        } catch (err) {
          showMessage('error', 'Failed to disconnect Gmail');
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const getHeaders = () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.error('No access token found in localStorage');
      return {};
    }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchClubs = async () => {
    try {
      const headers = getHeaders();
      const clubsRes = await axios.get(`${API}/clubs`, { headers });
      setClubs(clubsRes.data);
    } catch (err) {
      console.error('Failed to fetch clubs:', err);
    }
  };

  const fetchData = async () => {
    try {
      const headers = getHeaders();
      switch (activeTab) {
        case 'users':
          const usersRes = await axios.get(`${API}/users`, { headers });
          setUsers(usersRes.data);
          break;
        case 'speakers':
          const speakersRes = await axios.get(`${API}/speakers`, { headers });
          setSpeakers(speakersRes.data);
          break;
        case 'clubs':
          await fetchClubs();
          break;
        case 'topics':
          const topicsRes = await axios.get(`${API}/topics?include_skipped=true`, { headers });
          setTopics(topicsRes.data);
          break;
        case 'talks':
          const talksRes = await axios.get(`${API}/talks?include_skipped=true`, { headers });
          setTalks(talksRes.data);
          setSelectedTalkIds(new Set());
          break;
        case 'audit':
          const auditRes = await axios.get(`${API}/audit-logs`, { headers });
          setAuditLogs(auditRes.data);
          break;
        case 'analytics':
          const analyticsRes = await axios.get(`${API}/analytics?days=${analyticsDays}`, { headers });
          setAnalytics(analyticsRes.data);
          break;
      }
    } catch (err) {
      showMessage('error', 'Failed to fetch data');
    }
  };

  const parseErrorMessage = (error) => {
    // Handle Pydantic validation errors
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      
      // If detail is an array of validation errors
      if (Array.isArray(detail)) {
        return detail.map(err => {
          const field = err.loc ? err.loc.join('.') : 'field';
          return `${field}: ${err.msg}`;
        }).join(', ');
      }
      
      // If detail is a string
      if (typeof detail === 'string') {
        return detail;
      }
      
      // If detail is an object with a message
      if (detail.msg) {
        return detail.msg;
      }
    }
    
    // Fallback to generic message
    return error.message || 'Operation failed';
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const showDialogMessage = (type, text) => {
    setDialogMessage({ type, text });
    setTimeout(() => setDialogMessage(null), 5000);
  };

  const openDialog = (type, data = null) => {
    setDialog({ open: true, type, data });
    if (data) {
      setFormData({ ...data });
    } else {
      setFormData({});
    }
  };

  const closeDialog = () => {
    setDialog({ open: false, type: '', data: null });
    setFormData({});
    setDialogMessage(null);
  };

  // User Management
  const handleUserSubmit = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      if (dialog.type === 'add-user') {
        await axios.post(`${API}/users`, formData, { headers });
        showMessage('success', 'User created successfully');
        closeDialog();
        fetchData();
      } else if (dialog.type === 'edit-user') {
        await axios.put(`${API}/users/${dialog.data.id}`, formData, { headers });
        showMessage('success', 'User updated successfully');
        closeDialog();
        fetchData();
      }
    } catch (err) {
      showDialogMessage('error', parseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete User',
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      onConfirm: async () => {
        // Validate token before making request
        const token = getValidToken();
        if (!token) {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
          return;
        }
        
        try {
          console.log('Deleting user:', userId);
          await axios.delete(`${API}/users/${userId}`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          showMessage('success', 'User deleted successfully');
          fetchData();
        } catch (err) {
          console.error('Delete user error:', err.response?.status, err.response?.data);
          if (handleAuthError(err)) return;
          showMessage('error', parseErrorMessage(err));
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  // Speaker Management
  const handleSpeakerSubmit = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      if (dialog.type === 'add-speaker') {
        await axios.post(`${API}/speakers`, formData, { headers });
        showMessage('success', 'Speaker created successfully');
        closeDialog();
        fetchData();
      } else if (dialog.type === 'edit-speaker') {
        await axios.put(`${API}/speakers/${dialog.data.id}`, formData, { headers });
        showMessage('success', 'Speaker updated successfully');
        closeDialog();
        fetchData();
      }
    } catch (err) {
      showDialogMessage('error', parseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSpeaker = async (speakerId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Speaker',
      message: 'Are you sure you want to delete this speaker? This action cannot be undone.',
      onConfirm: async () => {
        // Validate token before making request
        const token = getValidToken();
        if (!token) {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
          return;
        }
        
        try {
          console.log('Deleting speaker:', speakerId);
          await axios.delete(`${API}/speakers/${speakerId}`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          showMessage('success', 'Speaker deleted successfully');
          fetchData();
        } catch (err) {
          console.error('Delete speaker error:', err.response?.status, err.response?.data);
          if (handleAuthError(err)) return;
          showMessage('error', parseErrorMessage(err));
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const openConsolidateDialog = () => {
    const selected = speakers.filter(s => selectedSpeakerIds.has(s.id));
    setConsolidateDialog({ open: true, speakers: selected, masterId: selected[0]?.id ?? null });
  };

  const handleConsolidate = async () => {
    const { masterId, speakers: dialogSpeakers } = consolidateDialog;
    const mergeIds = dialogSpeakers.filter(s => s.id !== masterId).map(s => s.id);
    const token = getValidToken();
    if (!token) return;
    try {
      const response = await axios.post(`${API}/speakers/consolidate`,
        { master_id: masterId, merge_ids: mergeIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConsolidateDialog({ open: false, speakers: [], masterId: null });
      setSelectedSpeakerIds(new Set());
      showMessage('success', response.data.message);
      fetchData();
    } catch (err) {
      showMessage('error', parseErrorMessage(err));
    }
  };

  // Club Management
  const handleClubSubmit = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      if (dialog.type === 'add-club') {
        await axios.post(`${API}/clubs`, formData, { headers });
        showMessage('success', 'Club created successfully');
        closeDialog();
        fetchData();
      } else if (dialog.type === 'edit-club') {
        await axios.put(`${API}/clubs/${dialog.data.id}`, formData, { headers });
        showMessage('success', 'Club updated successfully');
        closeDialog();
        fetchData();
      }
    } catch (err) {
      showMessage('error', parseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClub = async (clubId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Club',
      message: 'Are you sure you want to delete this club? This action cannot be undone.',
      onConfirm: async () => {
        // Validate token before making request
        const token = getValidToken();
        if (!token) {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
          return;
        }
        
        try {
          console.log('Deleting club:', clubId);
          await axios.delete(`${API}/clubs/${clubId}`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          showMessage('success', 'Club deleted successfully');
          fetchData();
        } catch (err) {
          console.error('Delete club error:', err.response?.status, err.response?.data);
          if (handleAuthError(err)) return;
          showMessage('error', parseErrorMessage(err));
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  // Add Talk
  const openAddTalkDialog = async () => {
    const headers = getHeaders();
    await Promise.all([
      axios.get(`${API}/speakers`, { headers }).then(r => setSpeakers(r.data)),
      axios.get(`${API}/topics?include_skipped=true`, { headers }).then(r => setTopics(r.data)),
      clubs.length === 0 ? axios.get(`${API}/clubs`, { headers }).then(r => setClubs(r.data)) : Promise.resolve(),
    ]);
    setAddTalkError(null);
    setAddTalkDialog({ open: true });
  };

  const handleAddTalkSubmit = async () => {
    const token = getValidToken();
    if (!token) return;
    setAddTalkError(null);

    const f = addTalkForm;
    if (f.speakerMode === 'existing' && !f.speakerId)   { setAddTalkError('Please select a speaker.'); return; }
    if (f.speakerMode === 'new'      && !f.speakerName) { setAddTalkError('Please enter the speaker name.'); return; }
    if (f.topicMode   === 'existing' && !f.topicId)     { setAddTalkError('Please select a topic.'); return; }
    if (f.topicMode   === 'new'      && !f.topicName)   { setAddTalkError('Please enter the topic name.'); return; }
    if (!f.clubName)   { setAddTalkError('Please select a club.'); return; }
    if (!f.eventDate)  { setAddTalkError('Please enter the delivery date.'); return; }

    const payload = { club_name: f.clubName, event_date: f.eventDate };
    if (f.speakerMode === 'existing') {
      payload.speaker_id = f.speakerId;
    } else {
      payload.speaker_name = f.speakerName;
      if (f.speakerEmail) payload.speaker_email = f.speakerEmail;
    }
    if (f.topicMode === 'existing') {
      payload.topic_id = f.topicId;
    } else {
      payload.topic_name = f.topicName;
      if (f.topicDescription) payload.topic_description = f.topicDescription;
    }

    try {
      const response = await axios.post(`${API}/talks`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAddTalkDialog({ open: false });
      setSpeakerPopoverOpen(false);
      setTopicPopoverOpen(false);
      setAddTalkForm({
        speakerMode: 'existing', speakerId: '', speakerName: '', speakerEmail: '',
        topicMode: 'existing', topicId: '', topicName: '', topicDescription: '',
        clubName: '', eventDate: '',
      });
      showMessage('success', response.data.message);
      fetchData();
    } catch (err) {
      setAddTalkError(parseErrorMessage(err));
    }
  };

  // Topic Management
  const handleTopicSubmit = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      if (dialog.type === 'add-topic') {
        await axios.post(`${API}/topics`, formData, { headers });
        showMessage('success', 'Topic created successfully');
        closeDialog();
        fetchData();
      } else if (dialog.type === 'edit-topic') {
        await axios.put(`${API}/topics/${dialog.data.id}`, formData, { headers });
        showMessage('success', 'Topic updated successfully');
        closeDialog();
        fetchData();
      }
    } catch (err) {
      if (err.response?.status === 409) {
        const existing = err.response.data.detail?.existing_topic;
        if (existing) {
          closeDialog();
          setTopicConsolidateDialog({
            open: true,
            topics: [existing, { id: dialog.data.id, name: dialog.data.name, description: dialog.data.description }],
            masterId: existing.id,
          });
          return;
        }
      }
      showMessage('error', parseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const openTopicConsolidateDialog = () => {
    const selected = topics.filter(t => selectedTopicIds.has(t.id));
    setTopicConsolidateDialog({ open: true, topics: selected, masterId: selected[0]?.id ?? null });
  };

  const handleConsolidateTopics = async () => {
    const { masterId, topics: dialogTopics } = topicConsolidateDialog;
    const mergeIds = dialogTopics.filter(t => t.id !== masterId).map(t => t.id);
    const token = getValidToken();
    if (!token) return;
    try {
      const response = await axios.post(
        `${API}/topics/consolidate`,
        { master_id: masterId, merge_ids: mergeIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTopicConsolidateDialog({ open: false, topics: [], masterId: null });
      setSelectedTopicIds(new Set());
      showMessage('success', response.data.message);
      fetchData();
    } catch (err) {
      showMessage('error', parseErrorMessage(err));
    }
  };

  const handleDeleteTopic = async (topicId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Topic',
      message: 'Are you sure you want to delete this topic? This action cannot be undone.',
      onConfirm: async () => {
        // Validate token before making request
        const token = getValidToken();
        if (!token) {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
          return;
        }
        
        try {
          console.log('Deleting topic:', topicId);
          await axios.delete(`${API}/topics/${topicId}`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          showMessage('success', 'Topic deleted successfully');
          fetchData();
        } catch (err) {
          console.error('Delete topic error:', err.response?.status, err.response?.data);
          if (handleAuthError(err)) return;
          showMessage('error', parseErrorMessage(err));
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const handleBulkDeleteTalks = () => {
    if (selectedTalkIds.size === 0) return;
    setConfirmDialog({
      open: true,
      title: 'Delete Selected Talks',
      message: `Are you sure you want to permanently delete ${selectedTalkIds.size} talk(s)? All associated delivery records and ratings will also be removed. This cannot be undone.`,
      onConfirm: async () => {
        const token = getValidToken();
        if (!token) {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
          return;
        }
        try {
          const response = await axios.delete(`${API}/talks/bulk-delete`, {
            headers: { Authorization: `Bearer ${token}` },
            data: { talk_ids: Array.from(selectedTalkIds) },
          });
          showMessage('success', response.data.message);
          fetchData();
        } catch (err) {
          showMessage('error', parseErrorMessage(err));
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  // Upload JSON
  const handleUploadJSON = async () => {
    if (!talksUploadFile) {
      showMessage('error', 'Please select a file');
      return;
    }

    // Validate token before making request
    const token = getValidToken();
    if (!token) {
      return; // getValidToken will handle logout
    }

    setLoading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', talksUploadFile);
      
      console.log('Uploading talks with token:', token.substring(0, 20) + '...');
      
      const response = await axios.post(`${API}/upload-talks`, uploadFormData, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', response.data.message);
      setTalksUploadFile(null);
      // Clear the file input
      const fileInput = document.getElementById('talks-upload');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      console.error('Upload error:', err.response?.status, err.response?.data);
      if (handleAuthError(err)) return;
      showMessage('error', parseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClubs = async () => {
    if (!clubsUploadFile) {
      showMessage('error', 'Please select a file');
      return;
    }

    // Validate token before making request
    const token = getValidToken();
    if (!token) {
      return; // getValidToken will handle logout
    }

    setLoading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', clubsUploadFile);
      
      const response = await axios.post(`${API}/upload-clubs`, uploadFormData, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', response.data.message);
      setClubsUploadFile(null);
      // Clear the file input
      const fileInput = document.getElementById('clubs-upload');
      if (fileInput) fileInput.value = '';
      fetchData();
    } catch (err) {
      console.error('Upload clubs error:', err.response?.status, err.response?.data);
      if (handleAuthError(err)) return;
      showMessage('error', parseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Backup/Restore
  const handleBackup = async () => {
    // Validate token before making request
    const token = getValidToken();
    if (!token) {
      return;
    }
    
    try {
      const response = await axios.get(`${API}/backup`, { 
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_${new Date().toISOString()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showMessage('success', 'Backup downloaded successfully');
    } catch (err) {
      console.error('Backup error:', err.response?.status, err.response?.data);
      if (handleAuthError(err)) return;
      showMessage('error', parseErrorMessage(err));
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) {
      showMessage('error', 'Please select a backup file');
      return;
    }

    setConfirmDialog({
      open: true,
      title: 'Restore Database',
      message: 'This will overwrite all existing data. Are you sure you want to continue?',
      onConfirm: async () => {
        // Validate token before making request
        const token = getValidToken();
        if (!token) {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
          return;
        }
        
        setLoading(true);
        try {
          const restoreFormData = new FormData();
          restoreFormData.append('file', restoreFile);
          
          await axios.post(`${API}/restore`, restoreFormData, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          showMessage('success', 'Database restored successfully');
          setRestoreFile(null);
          // Clear the file input
          const fileInput = document.getElementById('restore-file');
          if (fileInput) fileInput.value = '';
          fetchData();
        } catch (err) {
          console.error('Restore error:', err.response?.status, err.response?.data);
          if (handleAuthError(err)) return;
          showMessage('error', parseErrorMessage(err));
        } finally {
          setLoading(false);
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img src="/logo_6270.png" alt="Rotary District 6270" className="h-16 w-auto" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
                <p className="text-sm text-gray-500">Manage system content and users</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Database Status Badge */}
              <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center space-x-2 ${
                dbStatus.status === 'connected' 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : dbStatus.status === 'checking'
                  ? 'bg-gray-100 text-gray-600 border border-gray-200'
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`} title={dbStatus.message} data-testid="database-status-badge">
                <span className={`w-2 h-2 rounded-full ${
                  dbStatus.status === 'connected' 
                    ? 'bg-green-500 animate-pulse' 
                    : dbStatus.status === 'checking'
                    ? 'bg-gray-400'
                    : 'bg-red-500'
                }`}></span>
                <span>
                  Database: {dbStatus.status === 'connected' ? 'Connected' : dbStatus.status === 'checking' ? 'Checking...' : 'Disconnected'}
                </span>
              </div>
              
              <Button variant="outline" onClick={() => setAboutOpen(true)} data-testid="about-button">
                About...
              </Button>
              <Link to="/browse">
                <Button variant="outline" data-testid="browse-talks-link">Browse Talks</Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" data-testid="dashboard-link">Dashboard</Button>
              </Link>
              <Button variant="outline" onClick={onLogout} data-testid="logout-button">Logout</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16 w-full">
        {message && (
          <Alert className={`mb-6 alert-animate ${
            message.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
          }`} data-testid="admin-message-alert">
            <AlertDescription className={message.type === 'error' ? 'text-red-800' : 'text-green-800'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-8 w-full mb-6">
            <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
            <TabsTrigger value="speakers" data-testid="speakers-tab">Speakers</TabsTrigger>
            <TabsTrigger value="clubs" data-testid="clubs-tab">Clubs</TabsTrigger>
            <TabsTrigger value="topics" data-testid="topics-tab">Topics</TabsTrigger>
            <TabsTrigger value="talks" data-testid="talks-tab">Talks</TabsTrigger>
            <TabsTrigger value="upload" data-testid="upload-tab">Upload</TabsTrigger>
            <TabsTrigger value="audit" data-testid="audit-tab">Audit Logs</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">Analytics</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" data-testid="users-content">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>User Management</CardTitle>
                    <CardDescription>{users.length} users in system</CardDescription>
                  </div>
                  <Button onClick={() => openDialog('add-user')} className="bg-[#005DAA] hover:bg-[#004A8F]" data-testid="add-user-button">
                    Create User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Club</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Authorized</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {users.map((u, idx) => (
                        <tr key={u.id} data-testid={`user-row-${idx}`}>
                          <td className="px-4 py-3 text-sm">{u.first_name} {u.last_name}</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-700">{u.username}</td>
                          <td className="px-4 py-3 text-sm">{u.email}</td>
                          <td className="px-4 py-3 text-sm">{u.club}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              u.role === 'Administrator' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              u.is_verified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {u.is_verified ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm space-x-2">
                            <Button size="sm" variant="outline" onClick={() => openDialog('edit-user', u)} data-testid={`edit-user-${idx}`}>
                              Edit
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteUser(u.id)} data-testid={`delete-user-${idx}`}>
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Speakers Tab */}
          <TabsContent value="speakers" data-testid="speakers-content">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Speaker Management</CardTitle>
                    <CardDescription>{speakers.length} speakers in system</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="border-[#005DAA] text-[#005DAA]"
                      disabled={selectedSpeakerIds.size < 2}
                      onClick={openConsolidateDialog}
                      data-testid="consolidate-speakers-button"
                    >
                      Consolidate Selected ({selectedSpeakerIds.size})
                    </Button>
                    <Button onClick={() => openDialog('add-speaker')} className="bg-[#005DAA] hover:bg-[#004A8F]" data-testid="add-speaker-button">
                      Add Speaker
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Input
                    placeholder="Filter by name or email..."
                    value={speakerFilter}
                    onChange={(e) => setSpeakerFilter(e.target.value)}
                    data-testid="speaker-filter-input"
                  />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 w-8">
                          <input
                            type="checkbox"
                            data-testid="select-all-speakers"
                            checked={speakers.length > 0 && selectedSpeakerIds.size === speakers.filter(s => {
                              const q = speakerFilter.toLowerCase();
                              return !q || s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q);
                            }).length}
                            onChange={e => {
                              const visible = speakers.filter(s => {
                                const q = speakerFilter.toLowerCase();
                                return !q || s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q);
                              });
                              setSelectedSpeakerIds(e.target.checked ? new Set(visible.map(s => s.id)) : new Set());
                            }}
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {speakers.filter(s => {
                        const q = speakerFilter.toLowerCase();
                        return !q || s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q);
                      }).map((s, idx) => (
                        <tr key={s.id} data-testid={`speaker-row-${idx}`} className={selectedSpeakerIds.has(s.id) ? 'bg-blue-50' : ''}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              data-testid={`speaker-checkbox-${idx}`}
                              checked={selectedSpeakerIds.has(s.id)}
                              onChange={e => {
                                const next = new Set(selectedSpeakerIds);
                                if (e.target.checked) next.add(s.id); else next.delete(s.id);
                                setSelectedSpeakerIds(next);
                              }}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm">{s.name}</td>
                          <td className="px-4 py-3 text-sm">{s.email || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm space-x-2">
                            <Button size="sm" variant="outline" onClick={() => openDialog('edit-speaker', s)} data-testid={`edit-speaker-${idx}`}>
                              Edit
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteSpeaker(s.id)} data-testid={`delete-speaker-${idx}`}>
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clubs Tab */}
          <TabsContent value="clubs" data-testid="clubs-content">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Club Management</CardTitle>
                    <CardDescription>{clubs.length} clubs in system</CardDescription>
                  </div>
                  <Button onClick={() => openDialog('add-club')} className="bg-[#005DAA] hover:bg-[#004A8F]" data-testid="add-club-button">
                    Add Club
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Program Chair</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {clubs.map((c, idx) => (
                        <tr key={c.id} data-testid={`club-row-${idx}`}>
                          <td className="px-4 py-3 text-sm">{c.name}</td>
                          <td className="px-4 py-3 text-sm">{c.program_chair_name || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm">{c.program_chair_email || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm space-x-2">
                            <Button size="sm" variant="outline" onClick={() => openDialog('edit-club', c)} data-testid={`edit-club-${idx}`}>
                              Edit
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteClub(c.id)} data-testid={`delete-club-${idx}`}>
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Topics Tab */}
          <TabsContent value="topics" data-testid="topics-content">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Topic Management</CardTitle>
                    <CardDescription>{topics.length} topics in system</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="border-[#005DAA] text-[#005DAA]"
                      disabled={selectedTopicIds.size < 2}
                      onClick={openTopicConsolidateDialog}
                      data-testid="consolidate-topics-button"
                    >
                      Consolidate Selected ({selectedTopicIds.size})
                    </Button>
                    <Button onClick={() => openDialog('add-topic')} className="bg-[#005DAA] hover:bg-[#004A8F]" data-testid="add-topic-button">
                      Add Topic
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search Input */}
                <div className="mb-4">
                  <div className="relative">
                    <Input
                      placeholder="Search topics by name or description..."
                      value={topicSearch}
                      onChange={(e) => setTopicSearch(e.target.value)}
                      className="pl-10"
                      data-testid="topic-search-input"
                    />
                    <svg
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {topicSearch && (
                      <button
                        onClick={() => setTopicSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        data-testid="topic-search-clear"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {topicSearch && (
                    <p className="text-sm text-gray-500 mt-2" data-testid="topic-search-results">
                      Showing {topics.filter(t => 
                        t.name?.toLowerCase().includes(topicSearch.toLowerCase()) ||
                        t.description?.toLowerCase().includes(topicSearch.toLowerCase())
                      ).length} of {topics.length} topics
                    </p>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 w-8">
                          <input
                            type="checkbox"
                            checked={topics.length > 0 && selectedTopicIds.size === topics.length}
                            onChange={e => setSelectedTopicIds(e.target.checked ? new Set(topics.map(t => t.id)) : new Set())}
                            data-testid="topic-select-all"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skip</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {topics
                        .filter(t => {
                          if (!topicSearch) return true;
                          const search = topicSearch.toLowerCase();
                          return (
                            t.name?.toLowerCase().includes(search) ||
                            t.description?.toLowerCase().includes(search)
                          );
                        })
                        .map((t, idx) => (
                        <tr key={t.id} data-testid={`topic-row-${idx}`} className={t.skip ? 'bg-gray-100 opacity-60' : ''}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedTopicIds.has(t.id)}
                              onChange={e => {
                                const next = new Set(selectedTopicIds);
                                e.target.checked ? next.add(t.id) : next.delete(t.id);
                                setSelectedTopicIds(next);
                              }}
                              data-testid={`topic-checkbox-${idx}`}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm">{t.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={t.description || ''}>
                            {t.description || <span className="text-gray-400 italic">No description</span>}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {t.skip ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Hidden
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Visible
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm space-x-2">
                            <Button size="sm" variant="outline" onClick={() => openDialog('edit-topic', t)} data-testid={`edit-topic-${idx}`}>
                              Edit
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteTopic(t.id)} data-testid={`delete-topic-${idx}`}>
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Talks Tab */}
          <TabsContent value="talks" data-testid="talks-content">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Talks</CardTitle>
                    <CardDescription>{talks.length} talks in system</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      className="bg-[#005DAA] hover:bg-[#004A8F]"
                      onClick={openAddTalkDialog}
                      data-testid="add-talk-button"
                    >
                      Add Talk
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={selectedTalkIds.size === 0}
                      onClick={handleBulkDeleteTalks}
                      data-testid="delete-selected-talks-button"
                    >
                      Delete Selected ({selectedTalkIds.size})
                    </Button>
                  </div>
                </div>
                <div className="mt-3">
                  <Input
                    placeholder="Filter by speaker or topic..."
                    value={talkSearch}
                    onChange={e => setTalkSearch(e.target.value)}
                    data-testid="talk-search-input"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left pb-2 pr-3 w-8">
                        <input
                          type="checkbox"
                          data-testid="select-all-talks"
                          checked={talks.length > 0 && selectedTalkIds.size === talks.filter(t => {
                            const q = talkSearch.toLowerCase();
                            return !q || t.speaker_name?.toLowerCase().includes(q) || t.topic_name?.toLowerCase().includes(q);
                          }).length}
                          onChange={e => {
                            const visible = talks.filter(t => {
                              const q = talkSearch.toLowerCase();
                              return !q || t.speaker_name?.toLowerCase().includes(q) || t.topic_name?.toLowerCase().includes(q);
                            });
                            if (e.target.checked) {
                              setSelectedTalkIds(new Set(visible.map(t => t.id)));
                            } else {
                              setSelectedTalkIds(new Set());
                            }
                          }}
                        />
                      </th>
                      <th className="text-left pb-2">Speaker</th>
                      <th className="text-left pb-2">Topic</th>
                      <th className="text-left pb-2">Deliveries</th>
                      <th className="text-left pb-2">Avg Rating</th>
                      <th className="text-left pb-2">First Delivery</th>
                    </tr>
                  </thead>
                  <tbody>
                    {talks
                      .filter(t => {
                        const q = talkSearch.toLowerCase();
                        return !q || t.speaker_name?.toLowerCase().includes(q) || t.topic_name?.toLowerCase().includes(q);
                      })
                      .map((t, idx) => (
                        <tr key={t.id} data-testid={`talk-row-${idx}`} className={`border-b hover:bg-gray-50 ${selectedTalkIds.has(t.id) ? 'bg-blue-50' : ''}`}>
                          <td className="py-2 pr-3">
                            <input
                              type="checkbox"
                              data-testid={`talk-checkbox-${idx}`}
                              checked={selectedTalkIds.has(t.id)}
                              onChange={e => {
                                const next = new Set(selectedTalkIds);
                                if (e.target.checked) next.add(t.id);
                                else next.delete(t.id);
                                setSelectedTalkIds(next);
                              }}
                            />
                          </td>
                          <td className="py-2">{t.speaker_name}</td>
                          <td className="py-2">{t.topic_name}</td>
                          <td className="py-2">{t.delivery_count ?? 0}</td>
                          <td className="py-2">{t.average_rating ? t.average_rating.toFixed(1) : '—'}</td>
                          <td className="py-2">{t.original_date ?? '—'}</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
                {talks.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No talks found.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Upload Tab */}
          <TabsContent value="upload" data-testid="upload-content">
            {/* Gmail Configuration Card */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Email Configuration
                    </CardTitle>
                    <CardDescription>
                      Configure Gmail for sending verification emails to new users
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {gmailStatus.authenticated ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                        <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Not Connected
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {gmailStatus.authenticated ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">Sending emails as:</p>
                        <p className="text-gray-600">{gmailStatus.sender_email}</p>
                        {gmailStatus.last_updated && (
                          <p className="text-sm text-gray-500 mt-1">
                            Connected: {new Date(gmailStatus.last_updated).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={handleGmailDisconnect}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="gmail-disconnect-button"
                      >
                        Disconnect
                      </Button>
                    </div>
                    <Alert className="bg-green-50 border-green-200">
                      <AlertDescription className="text-green-800">
                        Email verification is active. New users will receive verification emails at registration.
                      </AlertDescription>
                    </Alert>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Alert className="bg-yellow-50 border-yellow-200">
                      <AlertDescription className="text-yellow-800">
                        Gmail is not connected. Verification emails are currently being logged but not sent. 
                        Connect Gmail to enable email verification for new users.
                      </AlertDescription>
                    </Alert>
                    <Button 
                      onClick={handleGmailConnect}
                      className="bg-[#005DAA] hover:bg-[#004A8F]"
                      data-testid="gmail-connect-button"
                    >
                      <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                      </svg>
                      Connect Gmail Account
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Upload Talk Events */}
              <Card>
                <CardHeader>
                  <CardTitle>Upload Talk Events</CardTitle>
                  <CardDescription>Upload JSON file with talk event records</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="talks-upload">Select JSON File</Label>
                    <Input
                      id="talks-upload"
                      type="file"
                      accept=".json"
                      onChange={(e) => setTalksUploadFile(e.target.files[0])}
                      data-testid="talks-upload-input"
                    />
                  </div>
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-blue-800 text-sm">
                      Expected format: Array of objects with fields: club, date, name (speaker), topic
                    </AlertDescription>
                  </Alert>
                  <Button
                    onClick={handleUploadJSON}
                    disabled={!talksUploadFile || loading}
                    className="w-full bg-[#005DAA] hover:bg-[#004A8F]"
                    data-testid="upload-talks-button"
                  >
                    {loading ? 'Uploading...' : 'Upload Talks'}
                  </Button>
                </CardContent>
              </Card>

              {/* Upload Clubs */}
              <Card>
                <CardHeader>
                  <CardTitle>Upload Clubs</CardTitle>
                  <CardDescription>Upload JSON file with club records</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="clubs-upload">Select JSON File</Label>
                    <Input
                      id="clubs-upload"
                      type="file"
                      accept=".json"
                      onChange={(e) => setClubsUploadFile(e.target.files[0])}
                      data-testid="clubs-upload-input"
                    />
                  </div>
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-blue-800 text-sm">
                      Expected format: Array of objects with fields: name (required), program_chair_name, program_chair_email
                    </AlertDescription>
                  </Alert>
                  <Button
                    onClick={handleUploadClubs}
                    disabled={!clubsUploadFile || loading}
                    className="w-full bg-[#005DAA] hover:bg-[#004A8F]"
                    data-testid="upload-clubs-button"
                  >
                    {loading ? 'Uploading...' : 'Upload Clubs'}
                  </Button>
                </CardContent>
              </Card>

              {/* Backup & Restore */}
              <Card>
                <CardHeader>
                  <CardTitle>Backup & Restore</CardTitle>
                  <CardDescription>Manage database backups</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Button
                      onClick={handleBackup}
                      className="w-full bg-[#F7A81B] hover:bg-[#E09517] text-gray-900"
                      data-testid="backup-button"
                    >
                      Download Backup
                    </Button>
                  </div>
                  <div className="border-t pt-4">
                    <Label htmlFor="restore-file">Restore from Backup</Label>
                    <Input
                      id="restore-file"
                      type="file"
                      accept=".json"
                      onChange={(e) => setRestoreFile(e.target.files[0])}
                      className="mt-2"
                      data-testid="restore-upload-input"
                    />
                    <Alert className="mt-2 bg-red-50 border-red-200">
                      <AlertDescription className="text-red-800 text-sm">
                        Warning: This will overwrite all existing data!
                      </AlertDescription>
                    </Alert>
                    <Button
                      onClick={handleRestore}
                      disabled={!restoreFile || loading}
                      variant="destructive"
                      className="w-full mt-2"
                      data-testid="restore-button"
                    >
                      {loading ? 'Restoring...' : 'Restore Database'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Audit Logs Tab */}
          <TabsContent value="audit" data-testid="audit-content">
            <Card>
              <CardHeader>
                <CardTitle>Audit Logs</CardTitle>
                <CardDescription>System activity logs (last 100)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {auditLogs.map((log, idx) => (
                        <tr key={log.id} data-testid={`audit-log-${idx}`}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">{log.action}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{log.user_id || 'N/A'}</td>
                          <td className="px-4 py-3">
                            {log.details && Object.keys(log.details).length > 0
                              ? JSON.stringify(log.details)
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" data-testid="analytics-content">
            {/* Period selector */}
            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm font-medium text-gray-600">Period:</span>
              {[7, 30, 90].map(d => (
                <button
                  key={d}
                  onClick={() => {
                    setAnalyticsDays(d);
                    const headers = getHeaders();
                    axios.get(`${API}/analytics?days=${d}`, { headers })
                      .then(r => setAnalytics(r.data))
                      .catch(() => showMessage('error', 'Failed to load analytics'));
                  }}
                  className={`px-3 py-1 rounded text-sm border transition-colors ${analyticsDays === d ? 'bg-[#005DAA] text-white border-[#005DAA]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                >
                  {d}d
                </button>
              ))}
            </div>

            {!analytics ? (
              <p className="text-center text-gray-500 py-12">Loading analytics…</p>
            ) : (
              <div className="space-y-6">

                {/* Summary stat cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Browse Sessions',  value: analytics.summary.browse_sessions,  color: 'bg-blue-50 border-blue-200 text-blue-700' },
                    { label: 'Booking Requests', value: analytics.summary.booking_requests, color: 'bg-green-50 border-green-200 text-green-700' },
                    { label: 'Ratings Given',    value: analytics.summary.ratings_submitted,color: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
                    { label: 'User Logins',      value: analytics.summary.user_logins,      color: 'bg-purple-50 border-purple-200 text-purple-700' },
                  ].map(({ label, value, color }) => (
                    <Card key={label} className={`border ${color.split(' ')[1]}`}>
                      <CardContent className="pt-6 pb-4 text-center">
                        <div className={`text-3xl font-bold ${color.split(' ')[2]}`}>{value}</div>
                        <div className="text-sm text-gray-500 mt-1">{label}</div>
                        <div className="text-xs text-gray-400">last {analytics.days} days</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Daily activity chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Daily Activity</CardTitle>
                    <CardDescription>Browse sessions, bookings, and ratings per day</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const maxVal = Math.max(1, ...analytics.daily_activity.map(d => d.browses + d.bookings + d.ratings));
                      const shown = analytics.days <= 30 ? analytics.daily_activity : analytics.daily_activity.filter((_, i) => i % 3 === 0);
                      return (
                        <div className="space-y-1">
                          <div className="flex gap-4 text-xs text-gray-500 mb-3">
                            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-blue-400"></span>Browses</span>
                            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-green-400"></span>Bookings</span>
                            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-yellow-400"></span>Ratings</span>
                          </div>
                          <div className="flex items-end gap-0.5 h-32">
                            {shown.map(day => {
                              const total = day.browses + day.bookings + day.ratings;
                              const heightPct = (total / maxVal) * 100;
                              return (
                                <div key={day.date} className="flex-1 flex flex-col justify-end group relative" title={`${day.date}\nBrowses: ${day.browses}\nBookings: ${day.bookings}\nRatings: ${day.ratings}`}>
                                  <div style={{ height: `${heightPct}%` }} className="w-full min-h-0 flex flex-col justify-end overflow-hidden rounded-t-sm">
                                    <div style={{ height: `${day.ratings / Math.max(1, total) * 100}%` }} className="w-full bg-yellow-400"></div>
                                    <div style={{ height: `${day.bookings / Math.max(1, total) * 100}%` }} className="w-full bg-green-400"></div>
                                    <div style={{ height: `${day.browses / Math.max(1, total) * 100}%` }} className="w-full bg-blue-400"></div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex justify-between text-xs text-gray-400 mt-1">
                            <span>{shown[0]?.date}</span>
                            <span>{shown[shown.length - 1]?.date}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  {/* Top searched topics */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Top Searched Topics</CardTitle>
                      <CardDescription>Topics entered in the browse filter</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {analytics.top_topics.length === 0 ? (
                        <p className="text-sm text-gray-400">No topic searches recorded yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {analytics.top_topics.map(({ topic, count }) => {
                            const pct = Math.round((count / analytics.top_topics[0].count) * 100);
                            return (
                              <div key={topic}>
                                <div className="flex justify-between text-sm mb-0.5">
                                  <span className="capitalize">{topic}</span>
                                  <span className="text-gray-500">{count}</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded h-2">
                                  <div className="bg-[#005DAA] h-2 rounded" style={{ width: `${pct}%` }}></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Action breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Action Breakdown</CardTitle>
                      <CardDescription>All system events in the period</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {analytics.action_breakdown.length === 0 ? (
                        <p className="text-sm text-gray-400">No activity recorded yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {analytics.action_breakdown.map(({ action, count }) => {
                            const pct = Math.round((count / analytics.action_breakdown[0].count) * 100);
                            return (
                              <div key={action}>
                                <div className="flex justify-between text-sm mb-0.5">
                                  <span className="font-mono text-xs text-gray-700">{action}</span>
                                  <span className="text-gray-500">{count}</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded h-2">
                                  <div className="bg-gray-400 h-2 rounded" style={{ width: `${pct}%` }}></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                </div>
              </div>
            )}
          </TabsContent>

        </Tabs>
      </main>

      {/* Edit/Add Dialogs */}
      <Dialog open={dialog.open} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialog.type === 'add-user' && 'Create User'}
              {dialog.type === 'edit-user' && `Edit ${dialog.data?.username} profile`}
              {dialog.type === 'add-speaker' && 'Add Speaker'}
              {dialog.type === 'edit-speaker' && 'Edit Speaker'}
              {dialog.type === 'add-club' && 'Add Club'}
              {dialog.type === 'edit-club' && 'Edit Club'}
              {dialog.type === 'add-topic' && 'Add Topic'}
              {dialog.type === 'edit-topic' && 'Edit Topic'}
            </DialogTitle>
          </DialogHeader>

          {/* Dialog Error Message */}
          {dialogMessage && (
            <Alert className={`${
              dialogMessage.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
            }`}>
              <AlertDescription className={dialogMessage.type === 'error' ? 'text-red-800' : 'text-green-800'}>
                {dialogMessage.text}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {/* User Form */}
            {(dialog.type === 'add-user' || dialog.type === 'edit-user') && (
              <>
                {dialog.type === 'add-user' && (
                  <div>
                    <Label>Username <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData.username || ''}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      data-testid="dialog-username"
                      placeholder="johndoe"
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData.first_name || ''}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      data-testid="dialog-first-name"
                    />
                  </div>
                  <div>
                    <Label>Last Name <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData.last_name || ''}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      data-testid="dialog-last-name"
                    />
                  </div>
                </div>
                <div>
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="dialog-email"
                  />
                </div>
                <div>
                  <Label>Club <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.club || ''}
                    onValueChange={(value) => setFormData({ ...formData, club: value })}
                  >
                    <SelectTrigger data-testid="dialog-club">
                      <SelectValue placeholder="Select club" />
                    </SelectTrigger>
                    <SelectContent>
                      {clubs.map((club) => (
                        <SelectItem key={club.id} value={club.name}>
                          {club.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Role <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.role || ''}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="dialog-role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="User">User</SelectItem>
                      <SelectItem value="Administrator">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label htmlFor="authorized-toggle">Authorized</Label>
                    <p className="text-sm text-gray-500">Allow user to log in</p>
                  </div>
                  <Switch
                    id="authorized-toggle"
                    checked={formData.is_verified !== undefined ? formData.is_verified : true}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_verified: checked })}
                    data-testid="dialog-authorized"
                  />
                </div>
                <div>
                  <Label>{dialog.type === 'add-user' ? 'Password' : 'New Password (leave empty to keep current)'}</Label>
                  <Input
                    type="password"
                    value={formData.password || ''}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    data-testid="dialog-password"
                    placeholder={dialog.type === 'add-user' ? 'Enter password' : ''}
                  />
                </div>
              </>
            )}

            {/* Speaker Form */}
            {(dialog.type === 'add-speaker' || dialog.type === 'edit-speaker') && (
              <>
                <div>
                  <Label>Name</Label>
                  <Input
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="dialog-speaker-name"
                  />
                </div>
                <div>
                  <Label>Email (optional)</Label>
                  <Input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="dialog-speaker-email"
                  />
                </div>
              </>
            )}

            {/* Club Form */}
            {(dialog.type === 'add-club' || dialog.type === 'edit-club') && (
              <>
                <div>
                  <Label>Club Name</Label>
                  <Input
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="dialog-club-name"
                  />
                </div>
                <div>
                  <Label>Program Chair Name (optional)</Label>
                  <Input
                    value={formData.program_chair_name || ''}
                    onChange={(e) => setFormData({ ...formData, program_chair_name: e.target.value })}
                    data-testid="dialog-program-chair-name"
                  />
                </div>
                <div>
                  <Label>Program Chair Email (optional)</Label>
                  <Input
                    type="email"
                    value={formData.program_chair_email || ''}
                    onChange={(e) => setFormData({ ...formData, program_chair_email: e.target.value })}
                    data-testid="dialog-program-chair-email"
                  />
                </div>
              </>
            )}

            {/* Topic Form */}
            {(dialog.type === 'add-topic' || dialog.type === 'edit-topic') && (
              <>
                <div>
                  <Label>Topic Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="dialog-topic-name"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description for this topic"
                    rows={3}
                    data-testid="dialog-topic-description"
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                  <div>
                    <Label className="text-sm font-medium">Hide from Browse</Label>
                    <p className="text-xs text-gray-500">When enabled, this topic won't appear in the public browse view</p>
                  </div>
                  <Switch
                    checked={formData.skip || false}
                    onCheckedChange={(checked) => setFormData({ ...formData, skip: checked })}
                    data-testid="dialog-topic-skip"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} data-testid="dialog-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (dialog.type === 'add-user') handleUserSubmit();
                else if (dialog.type === 'edit-user') handleUserSubmit();
                else if (dialog.type.includes('speaker')) handleSpeakerSubmit();
                else if (dialog.type === 'add-club') handleClubSubmit();
                else if (dialog.type === 'edit-club') handleClubSubmit();
                else if (dialog.type.includes('topic')) handleTopicSubmit();
              }}
              disabled={loading}
              className="bg-[#005DAA] hover:bg-[#004A8F]"
              data-testid="dialog-submit"
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consolidate Speakers Dialog */}
      <Dialog open={consolidateDialog.open} onOpenChange={(open) => !open && setConsolidateDialog({ open: false, speakers: [], masterId: null })}>
        <DialogContent data-testid="consolidate-dialog">
          <DialogHeader>
            <DialogTitle>Consolidate Speakers</DialogTitle>
            <DialogDescription>
              Select which record to keep as the master. All talks will be reassigned to the master speaker, and the other records will be deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-2">
            {consolidateDialog.speakers.map(s => (
              <label key={s.id} className={`flex items-start gap-3 p-3 rounded border cursor-pointer transition-colors ${consolidateDialog.masterId === s.id ? 'border-[#005DAA] bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input
                  type="radio"
                  name="master-speaker"
                  value={s.id}
                  checked={consolidateDialog.masterId === s.id}
                  onChange={() => setConsolidateDialog(d => ({ ...d, masterId: s.id }))}
                  className="mt-1"
                  data-testid={`consolidate-radio-${s.id}`}
                />
                <div>
                  <p className="font-medium text-sm">{s.name}</p>
                  <p className="text-xs text-gray-500">{s.email || 'No email'}</p>
                </div>
                {consolidateDialog.masterId === s.id && (
                  <span className="ml-auto text-xs font-semibold text-[#005DAA] uppercase">Master</span>
                )}
              </label>
            ))}
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setConsolidateDialog({ open: false, speakers: [], masterId: null })}>
              Cancel
            </Button>
            <Button
              className="bg-[#005DAA] hover:bg-[#004A8F]"
              disabled={!consolidateDialog.masterId}
              onClick={handleConsolidate}
              data-testid="consolidate-submit"
            >
              Consolidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consolidate Topics Dialog */}
      <Dialog open={topicConsolidateDialog.open} onOpenChange={(open) => !open && setTopicConsolidateDialog({ open: false, topics: [], masterId: null })}>
        <DialogContent data-testid="consolidate-topics-dialog">
          <DialogHeader>
            <DialogTitle>Consolidate Topics</DialogTitle>
            <DialogDescription>
              Select which record to keep as the master. All talks will be reassigned to the master topic, and the other records will be deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-2">
            {topicConsolidateDialog.topics.map(t => (
              <label key={t.id} className={`flex items-start gap-3 p-3 rounded border cursor-pointer transition-colors ${topicConsolidateDialog.masterId === t.id ? 'border-[#005DAA] bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input
                  type="radio"
                  name="master-topic"
                  value={t.id}
                  checked={topicConsolidateDialog.masterId === t.id}
                  onChange={() => setTopicConsolidateDialog(d => ({ ...d, masterId: t.id }))}
                  className="mt-1"
                  data-testid={`consolidate-topic-radio-${t.id}`}
                />
                <div>
                  <p className="font-medium text-sm">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.description || 'No description'}</p>
                </div>
                {topicConsolidateDialog.masterId === t.id && (
                  <span className="ml-auto text-xs font-semibold text-[#005DAA] uppercase">Master</span>
                )}
              </label>
            ))}
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setTopicConsolidateDialog({ open: false, topics: [], masterId: null })}>
              Cancel
            </Button>
            <Button
              className="bg-[#005DAA] hover:bg-[#004A8F]"
              disabled={!topicConsolidateDialog.masterId}
              onClick={handleConsolidateTopics}
              data-testid="consolidate-topics-submit"
            >
              Consolidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Talk Dialog */}
      <Dialog open={addTalkDialog.open} onOpenChange={(open) => !open && setAddTalkDialog({ open: false })}>
        <DialogContent className="sm:max-w-lg" data-testid="add-talk-dialog">
          <DialogHeader>
            <DialogTitle>Add Talk</DialogTitle>
            <DialogDescription>
              Record a talk delivery. Select an existing speaker and topic or enter new details.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {addTalkError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="add-talk-error">
                {addTalkError}
              </div>
            )}

            {/* ── Speaker ── */}
            <div className="space-y-2">
              <Label className="font-semibold">Speaker</Label>
              <div className="flex gap-2">
                {['existing', 'new'].map(mode => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => { setAddTalkForm(f => ({ ...f, speakerMode: mode, speakerId: '', speakerName: '', speakerEmail: '' })); setSpeakerPopoverOpen(false); }}
                    className={`px-3 py-1 rounded text-sm border transition-colors ${addTalkForm.speakerMode === mode ? 'bg-[#005DAA] text-white border-[#005DAA]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                    data-testid={`speaker-mode-${mode}`}
                  >
                    {mode === 'existing' ? 'Existing' : 'New'}
                  </button>
                ))}
              </div>
              {addTalkForm.speakerMode === 'existing' ? (
                <Popover open={speakerPopoverOpen} onOpenChange={setSpeakerPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-accent hover:text-accent-foreground"
                      data-testid="speaker-select-trigger"
                    >
                      <span className={addTalkForm.speakerId ? '' : 'text-muted-foreground'}>
                        {addTalkForm.speakerId
                          ? speakers.find(s => s.id === addTalkForm.speakerId)?.name ?? 'Select a speaker...'
                          : 'Select a speaker...'}
                      </span>
                      <span className="ml-2 opacity-50">▾</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                      <CommandInput placeholder="Search speakers..." />
                      <CommandList>
                        <CommandEmpty>No speaker found.</CommandEmpty>
                        <CommandGroup>
                          {[...speakers].sort((a, b) => a.name.localeCompare(b.name)).map(s => (
                            <CommandItem
                              key={s.id}
                              value={s.name + (s.email ? ' ' + s.email : '')}
                              onSelect={() => {
                                setAddTalkForm(f => ({ ...f, speakerId: s.id }));
                                setSpeakerPopoverOpen(false);
                              }}
                            >
                              {s.name}{s.email ? ` — ${s.email}` : ''}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              ) : (
                <div className="space-y-2">
                  <Input
                    placeholder="Full name *"
                    value={addTalkForm.speakerName}
                    onChange={e => setAddTalkForm(f => ({ ...f, speakerName: e.target.value }))}
                    data-testid="new-speaker-name"
                  />
                  <Input
                    placeholder="Email address (optional)"
                    value={addTalkForm.speakerEmail}
                    onChange={e => setAddTalkForm(f => ({ ...f, speakerEmail: e.target.value }))}
                    data-testid="new-speaker-email"
                  />
                </div>
              )}
            </div>

            {/* ── Topic ── */}
            <div className="space-y-2">
              <Label className="font-semibold">Topic</Label>
              <div className="flex gap-2">
                {['existing', 'new'].map(mode => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => { setAddTalkForm(f => ({ ...f, topicMode: mode, topicId: '', topicName: '', topicDescription: '' })); setTopicPopoverOpen(false); }}
                    className={`px-3 py-1 rounded text-sm border transition-colors ${addTalkForm.topicMode === mode ? 'bg-[#005DAA] text-white border-[#005DAA]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                    data-testid={`topic-mode-${mode}`}
                  >
                    {mode === 'existing' ? 'Existing' : 'New'}
                  </button>
                ))}
              </div>
              {addTalkForm.topicMode === 'existing' ? (
                <Popover open={topicPopoverOpen} onOpenChange={setTopicPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-accent hover:text-accent-foreground"
                      data-testid="topic-select-trigger"
                    >
                      <span className={addTalkForm.topicId ? '' : 'text-muted-foreground'}>
                        {addTalkForm.topicId
                          ? topics.find(t => t.id === addTalkForm.topicId)?.name ?? 'Select a topic...'
                          : 'Select a topic...'}
                      </span>
                      <span className="ml-2 opacity-50">▾</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                      <CommandInput placeholder="Search topics..." />
                      <CommandList>
                        <CommandEmpty>No topic found.</CommandEmpty>
                        <CommandGroup>
                          {[...topics].sort((a, b) => a.name.localeCompare(b.name)).map(t => (
                            <CommandItem
                              key={t.id}
                              value={t.name}
                              onSelect={() => {
                                setAddTalkForm(f => ({ ...f, topicId: t.id }));
                                setTopicPopoverOpen(false);
                              }}
                            >
                              {t.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              ) : (
                <div className="space-y-2">
                  <Input
                    placeholder="Topic name *"
                    value={addTalkForm.topicName}
                    onChange={e => setAddTalkForm(f => ({ ...f, topicName: e.target.value }))}
                    data-testid="new-topic-name"
                  />
                  <Textarea
                    placeholder="Description (optional)"
                    value={addTalkForm.topicDescription}
                    onChange={e => setAddTalkForm(f => ({ ...f, topicDescription: e.target.value }))}
                    rows={2}
                    data-testid="new-topic-description"
                  />
                </div>
              )}
            </div>

            {/* ── Delivery ── */}
            <div className="space-y-3">
              <Label className="font-semibold">Delivery</Label>
              <div>
                <Label className="text-sm text-gray-600">Club</Label>
                <Select value={addTalkForm.clubName} onValueChange={v => setAddTalkForm(f => ({ ...f, clubName: v }))}>
                  <SelectTrigger data-testid="club-select-trigger">
                    <SelectValue placeholder="Select a club..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clubs.map(c => (
                      <SelectItem key={c.id} value={c.name}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Date</Label>
                <Input
                  type="date"
                  value={addTalkForm.eventDate}
                  onChange={e => setAddTalkForm(f => ({ ...f, eventDate: e.target.value }))}
                  data-testid="event-date-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAddTalkDialog({ open: false })}>Cancel</Button>
            <Button className="bg-[#005DAA] hover:bg-[#004A8F]" onClick={handleAddTalkSubmit} data-testid="add-talk-submit">
              Add Talk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* About Modal */}
      <AboutModal open={aboutOpen} onOpenChange={setAboutOpen} />

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle>{confirmDialog.title}</DialogTitle>
            <DialogDescription>{confirmDialog.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}
              data-testid="confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmDialog.onConfirm) {
                  confirmDialog.onConfirm();
                }
              }}
              data-testid="confirm-delete"
            >
              Confirm Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

export default AdminPanel;
